import {IssueRelativeTime} from '@github-ui/ago'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {getAutomatedReviewer} from '@github-ui/hovercards/automated-reviewers'
import {getReviewerHovercardAttributes, type ReviewerHovercard} from '@github-ui/hovercards/reviewer-hovercard'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import type {ControlledTooltipProps} from '@github-ui/portal-tooltip/controlled'
import {ProfileReference} from '@github-ui/profile-reference'
import type {CommentAuthorAssociation as GraphQlCommentAuthorAssociation} from '@github-ui/server-persisted-queries/types/IssueViewerViewQuery'
import {DotFillIcon} from '@primer/octicons-react'
import {Label, Link, RelativeTime} from '@primer/react'
import {clsx} from 'clsx'
import {Suspense} from 'react'

import {LABELS} from '../constants/labels'
import {TEST_IDS} from '../constants/test-ids'
import styles from './ActivityHeader.module.css'
import avatarStyles from './Avatar.module.css'
import {CommentAuthorAssociation} from './CommentAuthorAssociation'
import {CommentHeaderBadge} from './CommentHeaderBadge'
import {CommentSubjectAuthor} from './CommentSubjectAuthor'
import {PinnedByBadge} from './PinnedByBadge'
import {SpammyBadge} from './SpammyBadge'
import {SponsorsBadge} from './SponsorsBadge'

type ActivityData = {
  authorAssociation: GraphQlCommentAuthorAssociation
  id: string
  createdAt: string
  isHidden: boolean
  minimizedReason: string | null | undefined
  pendingMinimizeReason?: string | null
  showSpammyBadge?: boolean
  repository: {
    id: string
    isPrivate: boolean
    name: string
    owner: {
      login: string
      url: string
    }
  }
  url: string
  createdViaEmail?: boolean
  viaApp?: {
    name: string
    url: string
  }
  authorToRepoOwnerSponsorship?: {
    createdAt: string
    isActive: boolean
  } | null
  state?: string
}

export type ActivityHeaderHeadingProps = {
  as: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
}

export const CommentAuthorTypes = {
  Bot: 'Bot',
  Mannequin: 'Mannequin',
  User: 'User',
} as const

export type CommentAuthorType = (typeof CommentAuthorTypes)[keyof typeof CommentAuthorTypes]

type ActivityHeaderProps = {
  actions?: React.ReactElement
  additionalHeaderMessage?: React.ReactElement
  additionalUserIcon?: React.ReactElement
  afterDateContent?: React.ReactElement
  badges?: React.ReactElement
  lastEditedByMessage?: React.ReactElement
  editHistoryComponent?: React.ReactElement
  avatarUrl: string
  comment: ActivityData
  commentSubjectAuthorLogin?: string
  commentSubjectType?: string
  commentRef?: React.RefObject<HTMLDivElement | null>
  isMinimized: boolean
  commentAuthorLogin: string
  commentAuthorProfileName?: string | null
  commentAuthorSlug?: string
  userAvatar?: React.ReactElement
  viewerDidAuthor?: boolean
  tooltipDirection?: ControlledTooltipProps['direction']
  isReply?: boolean
  commentAuthorType?: CommentAuthorType
  headingProps?: ActivityHeaderHeadingProps
  id?: string
  forceInlineAvatar?: boolean
  threadCommentCount?: number
  commentBody?: string
  isInDialogMode?: boolean
  originalDiffPathUri?: string | null
  isOutdated?: boolean
  hideProfileLink?: boolean
  pinnedByLogin?: string | null
  noContainerStyles?: boolean
}

type AuthorPresentation = {
  displayLogin: string
  hovercard?: ReviewerHovercard
  isAgent: boolean
  isBot: boolean
  isHandle: boolean
  profileUrl?: string
  showTypeLabel: boolean
  squareAvatar: boolean
}

function getAuthorPresentation({
  authorLogin,
  authorSlug,
  authorType,
  hideProfileLink,
}: {
  authorLogin: string
  authorSlug?: string
  authorType?: CommentAuthorType
  hideProfileLink: boolean
}): AuthorPresentation {
  const isBot = authorType === CommentAuthorTypes.Bot
  const isMannequin = authorType === CommentAuthorTypes.Mannequin
  const isAgent = isBot && ((authorLogin === 'Copilot' && !!authorSlug) || authorLogin === 'copilot-swe-agent')
  const automatedReviewer =
    isBot && !isAgent && isFeatureEnabled('automated_review_comment_reviewer_identity')
      ? getAutomatedReviewer(authorSlug)
      : undefined
  const displayLogin = isAgent ? 'Copilot' : (automatedReviewer?.displayName ?? authorLogin)
  const profileUrl = isMannequin || hideProfileLink ? undefined : `/${isBot ? 'apps/' : ''}${authorSlug ?? authorLogin}`

  let hovercard: ReviewerHovercard | undefined
  if (isAgent) {
    hovercard = {type: 'copilot', botSlug: authorSlug ?? authorLogin}
  } else if (automatedReviewer) {
    hovercard = isFeatureEnabled('automated_review_comment_reviewer_hovercard')
      ? {type: 'automated_reviewer', botSlug: automatedReviewer.slug}
      : undefined
  } else if (!isMannequin) {
    hovercard = {type: 'user', login: authorLogin}
  }

  return {
    displayLogin,
    hovercard,
    isAgent,
    isBot,
    isHandle: !isAgent && !automatedReviewer,
    profileUrl,
    showTypeLabel: isBot || isMannequin,
    squareAvatar: isAgent || !!automatedReviewer,
  }
}

function getOutdatedCommentUrl(originalDiffPathUri?: string | null) {
  if (!originalDiffPathUri) return undefined
  const url = new URL(originalDiffPathUri, window.location.origin)
  // Adding these params because without them it will navigate to the classic experience
  url.searchParams.set('new_files_changed', 'true')
  return url.toString()
}

/**
 * Abstraction of a comment header that can be used in different contexts.
 */
export function ActivityHeader({
  actions,
  additionalUserIcon,
  afterDateContent,
  badges,
  comment,
  commentAuthorType,
  commentAuthorSlug,
  commentAuthorLogin,
  commentAuthorProfileName,
  isMinimized = false,
  avatarUrl,
  lastEditedByMessage,
  editHistoryComponent,
  additionalHeaderMessage,
  viewerDidAuthor = false,
  userAvatar,
  commentSubjectAuthorLogin,
  commentSubjectType,
  isReply = false, // eslint-disable-next-line @eslint-react/no-unstable-default-props
  headingProps: {as: HeadingElement, ...headingProps} = {
    as: 'h3',
  },
  id,
  forceInlineAvatar = isFeatureEnabled('issue_inline_avatars'),
  originalDiffPathUri,
  isOutdated,
  hideProfileLink = false,
  pinnedByLogin,
  tooltipDirection,
  noContainerStyles = false,
}: ActivityHeaderProps) {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const showContainerStyles = !noContainerStyles && !isReply
  const commentMinimizedReason = comment.pendingMinimizeReason ?? comment.minimizedReason
  const commentHidden = !!commentMinimizedReason || comment.isHidden

  const authorPresentation = getAuthorPresentation({
    authorLogin: commentAuthorLogin,
    authorSlug: commentAuthorSlug,
    authorType: commentAuthorType,
    hideProfileLink,
  })
  const hovercardAttributes = getReviewerHovercardAttributes(
    authorPresentation.hovercard,
    getHovercardAttributesForType,
  )

  const showPinnedByBadge = pinnedByLogin && !commentHidden
  const createdAt = new Date(comment.createdAt)

  const subText = (
    <>
      {additionalHeaderMessage && <span className={styles.HeaderMutedText}>{additionalHeaderMessage}</span>}
      <span className={styles.HeaderMutedText}>
        <Link
          href={isOutdated ? (getOutdatedCommentUrl(originalDiffPathUri) ?? comment.url) : comment.url}
          data-turbo={isOutdated ? 'false' : 'true'}
          className={styles.HeaderLink}
        >
          <IssueRelativeTime datetime={comment.createdAt} />
        </Link>
        {comment.createdViaEmail && <span>{` ${LABELS.sentViaEmail}`}</span>}
        {comment.viaApp && (
          <span>
            {' '}
            – with{' '}
            <Link inline href={comment.viaApp.url}>
              {comment.viaApp.name}
            </Link>
          </span>
        )}
      </span>
      {afterDateContent}
      {commentHidden && (
        <span className={styles.HeaderMutedTextNoEllipsis}>
          {' '}
          &middot;{' '}
          {commentMinimizedReason ? (
            <span>
              {LABELS.hiddenCommentWithReason} {commentMinimizedReason.replace(/_/g, '-').toLowerCase()}
            </span>
          ) : (
            <span>{LABELS.hiddenComment}</span>
          )}
        </span>
      )}
      {lastEditedByMessage && !commentHidden && <Suspense>{lastEditedByMessage}</Suspense>}
      {showPinnedByBadge && <PinnedByBadge pinnedByLogin={pinnedByLogin} />}
    </>
  )
  return (
    <div
      id={id}
      data-testid={TEST_IDS.commentHeader}
      className={clsx(
        styles.ActivityHeaderContainer,
        showContainerStyles && styles.containerBase,
        showContainerStyles && viewerDidAuthor && styles.containerViewerDidAuthor,
        showContainerStyles && isMinimized && styles.containerMinimized,
        showContainerStyles && forceInlineAvatar && styles.containerForceInlineAvatarPadding,
      )}
    >
      <div
        className={clsx(
          styles.activityHeader,
          styles.ActivityHeaderGridLayout,
          !lastEditedByMessage && !editHistoryComponent && !forceInlineAvatar && styles.gridLayoutPaddingBottom,
        )}
      >
        <HeadingElement {...headingProps} className="sr-only">
          <>
            {authorPresentation.displayLogin} {isReply ? 'replied' : 'commented'}{' '}
            <RelativeTime date={createdAt}>
              on{' '}
              {createdAt.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}{' '}
            </RelativeTime>
          </>
        </HeadingElement>
        <div
          className={clsx(
            !forceInlineAvatar && avatarStyles.avatarInner,
            styles.AvatarContainer,
            !forceInlineAvatar && styles.avatarHiddenOnMedium,
          )}
        >
          {userAvatar ?? (
            <DefaultAvatar
              avatarUrl={avatarUrl}
              profileLabel={authorPresentation.displayLogin}
              isHandle={authorPresentation.isHandle}
              square={authorPresentation.squareAvatar}
              isHidden={commentHidden}
              hovercardAttributes={hovercardAttributes}
              profileUrl={authorPresentation.profileUrl}
            />
          )}
          {additionalUserIcon}
        </div>
        <div
          className={clsx(styles.narrowViewportWrapper, styles.CommentHeaderContentContainer)}
          data-testid={TEST_IDS.commentHeaderLeftSideItems}
        >
          <div className={styles.TitleContainer}>
            {authorPresentation.profileUrl ? (
              <Link
                href={authorPresentation.profileUrl}
                data-testid={TEST_IDS.avatarLink}
                {...hovercardAttributes}
                className={clsx(
                  styles.AuthorName,
                  styles.AuthorLink,
                  commentHidden ? 'color-fg-muted' : 'color-fg-default',
                )}
              >
                <ProfileReference
                  login={authorPresentation.displayLogin}
                  profileName={commentAuthorProfileName}
                  isAgent={authorPresentation.isAgent}
                />
              </Link>
            ) : (
              // We use a span to avoid accessibility issues with empty links
              <span
                data-testid={TEST_IDS.avatarName}
                {...hovercardAttributes}
                className={clsx(styles.AuthorName, commentHidden ? 'color-fg-muted' : 'color-fg-default')}
              >
                <ProfileReference
                  login={authorPresentation.displayLogin}
                  profileName={commentAuthorProfileName}
                  isAgent={authorPresentation.isAgent}
                />
              </span>
            )}

            {authorPresentation.showTypeLabel && (
              <Label variant="secondary" className="ml-1">
                {LABELS.authorLabel(authorPresentation.isBot, authorPresentation.isAgent)}
              </Label>
            )}
          </div>
          <div className={clsx(styles.footer, styles.FooterContainer)}>{subText}</div>
        </div>
        <div
          data-testid={TEST_IDS.commentHeaderRightSideItems}
          className={clsx(styles.narrowViewportWrapper, styles.ActionsContainer)}
        >
          {editHistoryComponent && <div className={styles.EditsContainer}>{editHistoryComponent}</div>}
          <div className={styles.BadgesContainer}>
            <div className={styles.BadgesGroupContainer}>
              {comment.state?.toUpperCase() === 'PENDING' && (
                <CommentHeaderBadge
                  leadingElement={<DotFillIcon />}
                  label="Pending"
                  ariaLabel="This review comment is pending"
                  testId="pending-badge"
                  variant="attention"
                  className={styles.CommentHeaderBadge}
                />
              )}
              {!commentHidden &&
                !isMinimized &&
                comment.authorToRepoOwnerSponsorship &&
                comment.authorToRepoOwnerSponsorship.isActive && (
                  <SponsorsBadge
                    createdAt={comment.authorToRepoOwnerSponsorship.createdAt}
                    owner={comment.repository.owner.login}
                    viewerDidAuthor={viewerDidAuthor}
                  />
                )}
              {!commentHidden && !isMinimized && comment.showSpammyBadge && <SpammyBadge />}
              {!comment.repository.isPrivate && !commentHidden && !isMinimized && (
                <CommentAuthorAssociation
                  association={comment.authorAssociation}
                  viewerDidAuthor={viewerDidAuthor}
                  org={comment.repository.owner.login}
                  repo={comment.repository.name}
                  tooltipDirection={tooltipDirection}
                />
              )}
              {commentAuthorLogin === commentSubjectAuthorLogin && (
                <CommentSubjectAuthor
                  viewerDidAuthor={viewerDidAuthor}
                  subjectType={commentSubjectType}
                  tooltipDirection={tooltipDirection}
                />
              )}
              {badges}
            </div>
            <div className={styles.ActionsButtonsContainer}>{actions}</div>
          </div>
        </div>
      </div>
    </div>
  )
}

function DefaultAvatar({
  avatarUrl,
  profileLabel,
  isHidden,
  isHandle,
  square,
  hovercardAttributes,
  profileUrl,
}: {
  avatarUrl: string
  profileLabel: string
  isHidden: boolean
  isHandle: boolean
  square: boolean
  hovercardAttributes: Record<string, string>
  profileUrl?: string
}) {
  const accessibleLabel = isHandle ? `@${profileLabel}` : profileLabel
  const avatarElement = (
    <GitHubAvatar
      src={avatarUrl}
      alt={accessibleLabel}
      size={24}
      square={square}
      className={isHidden ? avatarStyles.hiddenActivityAvatar : avatarStyles.activityAvatar}
    />
  )

  if (profileUrl) {
    return (
      <Link
        href={profileUrl}
        className={avatarStyles.avatarLink}
        {...hovercardAttributes}
        aria-label={`${accessibleLabel}'s profile`}
      >
        {avatarElement}
      </Link>
    )
  }

  // For non-interactive avatars (mannequins, etc.), don't use link with aria-label to prevent accessibility issues
  return (
    <div className={avatarStyles.avatarWithoutLink} {...hovercardAttributes}>
      {avatarElement}
    </div>
  )
}
